import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import {
  getLocaleDir,
  setCatalogLocales,
  type CatalogLocale,
} from './catalogLocales';

// Import translation files
import englishClans from './locales/english/clansEN.json';
import englishCommon from './locales/english/commonEN.json';
import englishComingSoon from './locales/english/comingSoonEN.json';
import englishHome from './locales/english/homeEN.json';
import englishFooter from './locales/english/footerEN.json';
import englishFeedback from './locales/english/feedbackEN.json';
import englishContact from './locales/english/contactEN.json';
import englishTournamentAdmin from './locales/english/tournamentAdminEN.json';
import englishNotifications from './locales/english/notificationsEN.json';
import englishProfile from './locales/english/profileEN.json';
import englishOrganizations from './locales/english/organizationsEN.json';
import englishJoyride from './locales/english/joyrideEN.json';
import englishNav from './locales/english/navEN.json';
import englishFeed from './locales/english/feedEN.json';
import englishChat from './locales/english/chatEN.json';
import englishAcademy from './locales/english/academyEN.json';
import englishTeam from './locales/english/teamEN.json';
import englishTeamAction from './locales/english/teamaction.json';
import englishteamsection from './locales/english/teamsectionEN.json';
import englishInvite from './locales/english/inviteEN.json';
import englishTournament from './locales/english/tournamentEN.json';
import englishEntryFeePrize from './locales/english/entryfeeprizeEN.json';
import englishMatches from './locales/english/matchesEN.json';
import englishRegistration from './locales/english/registrationEN.json';
import englishWallet from './locales/english/walletEN.json';
import englishAuth from './locales/english/authEN.json';
import englishCommonApiErrors from './locales/english/commonApiErrorsEN.json';
import englishProfileApiErrors from './locales/english/profileApiErrorsEN.json';
import englishWalletApiErrors from './locales/english/walletApiErrorsEN.json';
import englishTournamentApiErrors from './locales/english/tournamentApiErrorsEN.json';
import englishOrganizationApiErrors from './locales/english/organizationApiErrorsEN.json';
import englishTeamApiErrors from './locales/english/teamApiErrorsEN.json';
import englishMatchApiErrors from './locales/english/matchApiErrorsEN.json';
import englishClanApiErrors from './locales/english/clanApiErrorsEN.json';
import englishAcademyApiErrors from './locales/english/academyApiErrorsEN.json';
import englishInviteApiErrors from './locales/english/inviteApiErrorsEN.json';
import englishNotificationApiErrors from './locales/english/notificationApiErrorsEN.json';
import englishChatApiErrors from './locales/english/chatApiErrorsEN.json';

// ─── CDN-served catalog (Arabic + future migrated namespaces) ────────────────
// The 7 bundled languages below stay in `resources` and render instantly. With
// `partialBundledLanguages`, i18next ALSO calls the HTTP backend for any
// (language, namespace) NOT in the bundle — i.e. Arabic — so it loads from the
// CDN and can never affect the bundled languages. Bundles are immutable at
// v{N}, so the browser HTTP cache is the cache. We read the current version
// from the manifest and refresh in the background.
const I18N_CDN_BASE = 'https://ff.espotz.live';
const I18N_ENV = import.meta.env.VITE_APP_ENV === 'production' ? 'prod' : 'dev';
const I18N_VERSION_KEY = 'i18nCatalogVersion';
// Languages compiled into the JS bundle — never fetched from the CDN. Since S7 this is English
// only: it stays bundled purely as the offline fallback, so an unreachable CDN degrades to an
// English UI rather than raw translation keys. Every other language loads from the catalog, which
// is also why they must be reloaded when the catalog version changes (see below).
const BUNDLED_LOCALES = ['en'];

// API error codes, split by the page that can raise them so no one file grows unreadable.
// Kept as a const (not inlined below) — scripts/i18n-check.ts parses the `en:` block by brace.
const englishApiErrors = {
  ...englishCommonApiErrors,
  ...englishProfileApiErrors,
  ...englishWalletApiErrors,
  ...englishTournamentApiErrors,
  ...englishOrganizationApiErrors,
  ...englishTeamApiErrors,
  ...englishMatchApiErrors,
  ...englishClanApiErrors,
  ...englishAcademyApiErrors,
  ...englishInviteApiErrors,
  ...englishNotificationApiErrors,
  ...englishChatApiErrors,
};

let catalogVersion = Number(localStorage.getItem(I18N_VERSION_KEY)) || 1;

const bundleUrl = (lng: string, ns: string) =>
  `${I18N_CDN_BASE}/${I18N_ENV}/i18n/v${catalogVersion}/${lng}/${ns}.json`;

i18n.use(LanguageDetector); // Enable automatic language detection when flag is on
i18n.use(HttpBackend); // Load CDN-served locales (e.g. Arabic) not in the static bundle

i18n
  .use(initReactI18next) // Passes i18n down to react-i18next
  .init({
    resources: {
      en: {
        clans: englishClans,
        common: englishCommon,
        apiErrors: englishApiErrors,
        comingSoon: englishComingSoon,
        home: englishHome,
        footer: englishFooter,
        feedback: englishFeedback,
        contact: englishContact,
        tournamentAdmin: englishTournamentAdmin,
        notifications: englishNotifications,
        profile: englishProfile,
        nav: englishNav,
        academy: englishAcademy,
        team: englishTeam, // Team translations
        teamaction: englishTeamAction,
        teamsection: englishteamsection,
        invite: englishInvite,
        organizations: englishOrganizations,
        joyride: englishJoyride,
        tournament: englishTournament,
        entryfeeprize: englishEntryFeePrize,
        matches: englishMatches,
        registration: englishRegistration,
        wallet: englishWallet,
        auth: englishAuth,
        feed: englishFeed,
        chat: englishChat,
      },
    },
    fallbackLng: 'en', // fallback language
    supportedLngs: ['en', 'id', 'ph', 'vi', 'th', 'pt', 'es', 'ar'], // supported languages (ar served from CDN)

    // Keep the bundled `resources` above serving the 7 languages, AND let the HTTP
    // backend fill in any (lng, ns) missing from the bundle (Arabic). Missing keys
    // fall back to `en`, so Arabic can ship incrementally.
    partialBundledLanguages: true,
    backend: {
      loadPath: (lngs: string[], nss: string[]) => bundleUrl(lngs[0], nss[0]),
    },

    ns: [
      'clans',
      'common',
      'apiErrors',
      'nav',
      'academy',
      'team',
      'invite',
      'organizations',
      'joyride',
      'tournament',
      'entryfeeprize',
      'matches',
      'registration',
      'wallet',
      'auth',
      'feed',
      'chat',
    ], // Namespaces
    defaultNS: 'common', // default namespace

    detection: {
      order: ['localStorage', 'navigator'], // Check localStorage first, then browser
      caches: ['localStorage'], // Save detected language to localStorage
      lookupLocalStorage: 'language', // Key name in localStorage
    },

    interpolation: {
      escapeValue: false, // react already safes from XSS
    },
    react: {
      useSuspense: false, // disable suspense for now
    },
  });

// ─── RTL: drive <html dir/lang> from the active language ─────────────────────
// i18next knows a locale's direction (i18n.dir('ar') === 'rtl'), so this covers any
// future RTL language (he/fa/ur) for free. CSS logical properties mirror off this, but
// Radix primitives do NOT — they read RadixDirectionProvider, not the inherited dir.
const applyDocumentDirection = (lng: string) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = getLocaleDir(lng) ?? i18n.dir(lng);
  document.documentElement.lang = lng;
};
i18n.on('languageChanged', applyDocumentDirection);
i18n.on('initialized', () => applyDocumentDirection(i18n.language || 'en'));
applyDocumentDirection(i18n.language || 'en');

// Background: read the published manifest. It is the source of truth for BOTH the catalog
// version and which languages exist, so enabling a language in the CMS and publishing makes it
// appear in the switcher without a frontend release.
void (async () => {
  try {
    const res = await fetch(`${I18N_CDN_BASE}/${I18N_ENV}/i18n/manifest.json`, {
      cache: 'no-cache',
    });
    if (!res.ok) return;
    const manifest = (await res.json()) as {
      version?: number;
      locales?: Record<
        string,
        { name?: string; nativeName?: string; dir?: 'ltr' | 'rtl' }
      >;
    };

    if (manifest.locales && Object.keys(manifest.locales).length > 0) {
      const locales: CatalogLocale[] = Object.entries(manifest.locales)
        .map(([code, meta]) => ({
          code,
          name: meta.name ?? code,
          nativeName: meta.nativeName ?? meta.name ?? code,
          dir: meta.dir === 'rtl' ? ('rtl' as const) : ('ltr' as const),
        }))
        .sort((a, b) => a.code.localeCompare(b.code));
      setCatalogLocales(locales);

      // supportedLngs is fixed at init, and i18next refuses to switch to a language outside it —
      // so a CMS-added language would be listed but unselectable. Widen it to whatever the
      // backend publishes, keeping the bundled fallback in place.
      const supported = [
        ...new Set([...BUNDLED_LOCALES, ...locales.map(l => l.code), 'cimode']),
      ];
      i18n.options.supportedLngs = supported;
      // LanguageUtils snapshots supportedLngs in its constructor and `isSupportedCode` reads that
      // copy, not `options` — so widening options alone leaves every newly published language
      // rejected as unsupported. changeLanguage then resolves to the fallback, loads nothing and
      // renders English without an error. Both have to be set.
      const languageUtils = (
        i18n as unknown as {
          services?: { languageUtils?: { supportedLngs: string[] } };
        }
      ).services?.languageUtils;
      if (languageUtils) languageUtils.supportedLngs = supported;

      // A language the backend has since disabled must not strand the user on it.
      if (
        i18n.language &&
        !locales.some(l => l.code === i18n.language) &&
        !BUNDLED_LOCALES.includes(i18n.language)
      ) {
        void i18n.changeLanguage('en');
      } else {
        applyDocumentDirection(i18n.language || 'en');
      }
    }

    if (
      typeof manifest.version === 'number' &&
      manifest.version !== catalogVersion
    ) {
      catalogVersion = manifest.version;
      localStorage.setItem(I18N_VERSION_KEY, String(catalogVersion));
      if (i18n.language && !BUNDLED_LOCALES.includes(i18n.language)) {
        void i18n.reloadResources(i18n.language);
      }
    }
  } catch {
    // CDN unreachable — keep the cached version and the bundled locale list. English still renders
    // from the bundle, and any other language falls back to English rather than showing raw keys.
  }
})();

export default i18n;
